/**
 * RecurrencePicker — 3-tier progressive disclosure UI for recurrence rules.
 *
 * Tier 1 (Quick):    One-tap presets — Daily / Weekly / Monthly / Yearly + NL hint
 * Tier 2 (Custom):   Full frequency / interval / weekday / monthly-nth / after-completion
 * Tier 3 (Advanced): Pile-up, flexible goal, progressive overload, target container
 *
 * Generates an RRULE string + DbRecurrence config for the template task.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { parseRecurrenceNL } from "@/db/operations"
import { useTranslation } from "react-i18next"
import { RRule } from "rrule"
import dayjs from "dayjs"
import { cn } from "@/shared/utils/general/cn"
import { useLiveQueryImmediate } from "@/db/hooks"
import { db } from "@/db/schema"
import { useSetting } from "@/features/settings/hooks/useSetting"
import { aiRefusalMessage } from "@/features/ai-chat/aiQuotaDisplay"
import type { CreateRecurrenceOpts } from "@totodo/operations"
import { ContainerPicker } from "@/features/task-detail/components/ContainerPicker"
import { RecurringReminderPicker, formatReminderOffsetWithTime } from "./RecurringReminderPicker"
import type { ReminderOffset } from "./RecurringReminderPicker"
import { ResponsiveSheet } from "@/app/components/ui/responsive-sheet"
import { RECURRENCE_TEMPLATES, TEMPLATE_CATEGORIES, type RecurrenceTemplate } from "./RecurrenceTemplates"
import { InfiniteCalendar } from "@/features/schedule/InfiniteCalendar"
import { Switch } from "@/app/components/ui/switch"
import { Popover, PopoverTrigger, PopoverContent } from "@/app/components/ui/popover"
import ChevronLeftIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/chevron-left.svg?react"
import RepeatIcon from "@/shared/icons/pikaicons/stroke/arrows-chevrons/repeat-square.svg?react"
import LightningIcon from "@/shared/icons/pikaicons/stroke/weather/lightning-thunder-electric-on.svg?react"
import BellIcon from "@/shared/icons/pikaicons/stroke/alerts/notification-bell-on.svg?react"
import BellAddIcon from "@/shared/icons/pikaicons/solid/alerts/notification-bell-add.svg?react"
import AlarmIcon from "@/shared/icons/pikaicons/stroke/time/alarm-default.svg?react"
import CrossIcon from "@/shared/icons/totodoicons/stroke/cross.svg?react"
import FlagIcon from "@/shared/icons/pikaicons/stroke/general/flag.svg?react"
import FlagAddIcon from "@/shared/icons/pikaicons/solid/general/flag.svg?react"
import GridIcon from "@/shared/icons/pikaicons/stroke/general/grid-dashboard-bento.svg?react"
import PauseIcon from "@/shared/icons/totodoicons/solid/pause.svg?react"
import PlayIcon from "@/shared/icons/pikaicons/stroke/media/play-big.svg?react"
import TrashIcon from "@/shared/icons/totodoicons/stroke/delete.svg?react"
import SparkleAiIcon from "@/shared/icons/pikaicons/stroke/general/sparkle-ai-01.svg?react"
import SettingsIcon from "@/shared/icons/pikaicons/stroke/general/settings-02.svg?react"
import { AiPreviewModal, createAiPreviewTask, type AiPreviewResult } from "./AiPreviewModal"
import { apiRequest } from "@/shared/utils/api/apiClient"

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type RecurrenceFrequency = "daily" | "weekly" | "monthly" | "yearly"
export type RecurrenceMode = "FIXED" | "AFTER_COMPLETION"
export type PilePolicy = "ALLOW" | "NO_PILE" | "CAP"

export interface RecurrencePickerProps {
  templateId: string
  /** Template task's parentPath — used for AI preview container display */
  templateParentPath?: string
  initialConfig?: Partial<RecurrenceConfig>
  /** Task's scheduled start time (UTC ms) — used to show actual reminder times */
  taskStartAt?: number
  onSave: (config: RecurrenceConfig) => void
  onCancel: () => void
  /** If provided, shows a Pause/Resume button next to Save */
  onPause?: () => void
  /** Whether the recurrence is currently paused (shows Play icon if true) */
  isPaused?: boolean
  /** If provided, shows a Delete button on the right side of Save row */
  onDelete?: () => void
}

export interface RecurrenceConfig {
  mode: RecurrenceMode
  frequency: RecurrenceFrequency
  interval: number
  weekdays: number[] // 0=MO, 1=TU, ... 6=SU (RRule.MO etc)
  weekdayTimes?: Record<number, string> // Per-day time override: { 0: "07:00", 4: "09:00" }
  monthDay?: number
  monthWeek?: number // 1st, 2nd, -1 (last)
  monthWeekday?: number // 0=MO...6=SU
  afterCompletionDays: number
  afterCompletionTime?: string // "HH:mm"
  pilePolicy: PilePolicy
  pileCap: number
  endType: "never" | "count" | "date"
  endCount?: number
  endDate?: string // UTC ms
  reminderOffsets: Array<{ offsetMs: number; kind: "reminder" | "alarm" }>
  deadlineOffset?: string
  showInTodayDaysBefore?: number
  // Target container — instances placed here instead of template's container
  targetContainerId?: string
  // Flexible window (goal-based)
  flexibleCount?: number // e.g. 3
  flexiblePeriod?: "week" | "month"
  flexibleResetDay?: number // 0=Monday
  // Progressive overload
  progressiveField?: string
  progressiveStart?: number
  progressiveIncrement?: number
  progressiveUnit?: string
  progressiveMax?: number
  progressivePer?: "instance" | "week" | "month"
  // Focus
  autoFocus?: boolean
  // AI Enrichment
  aiEnabled?: boolean
  aiPrompt?: string
  aiCreativity?: "focused" | "creative" | "wild"
  aiAvoidRepeat?: boolean
  aiDetailLevel?: "short" | "normal" | "detailed"
}

const DEFAULT_CONFIG: RecurrenceConfig = {
  mode: "FIXED",
  frequency: "weekly",
  interval: 1,
  weekdays: [],
  afterCompletionDays: 1,
  pilePolicy: "NO_PILE",
  pileCap: 3,
  endType: "never",
  reminderOffsets: [],
}

// ---------------------------------------------------------------------------
// Weekday labels
// ---------------------------------------------------------------------------

const WEEKDAY_LABELS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"] as const
const MONTH_WEEK_LABELS = ["First", "Second", "Third", "Fourth", "Last"] as const
const MONTH_WEEK_VALUES = [1, 2, 3, 4, -1] as const

/**
 * Get the starting index into WEEKDAY_LABELS from the #week-start setting.
 * Setting: "0"=Sunday, "1"=Monday ... "6"=Saturday, "-1"=system.
 * WEEKDAY_LABELS: 0=Mon, 1=Tue ... 5=Sat, 6=Sun.
 */
function getWeekStartIndex(settingValue: string): number {
  let dayIndex: number
  if (settingValue === "-1") {
    // System detection
    try {
      const locale = new Intl.Locale(Intl.DateTimeFormat().resolvedOptions().locale)
      const info =
        (locale as unknown as { weekInfo?: { firstDay: number } }).weekInfo ??
        (locale as unknown as { getWeekInfo?: () => { firstDay: number } }).getWeekInfo?.()
      // Intl firstDay: 1=Mon..7=Sun
      dayIndex = info ? (info.firstDay === 7 ? 0 : info.firstDay) : 1
    } catch {
      dayIndex = 1 // Monday fallback
    }
  } else {
    dayIndex = Number(settingValue)
  }
  // Convert from Sunday=0 based to WEEKDAY_LABELS index (Mon=0 based)
  // Sunday(0) → 6, Monday(1) → 0, Tuesday(2) → 1, etc.
  return (dayIndex - 1 + 7) % 7
}

/** Returns reordered indices into WEEKDAY_LABELS based on week start setting */
function getOrderedWeekdays(weekStartSetting: string): number[] {
  const startIdx = getWeekStartIndex(weekStartSetting)
  return Array.from({ length: 7 }, (_, i) => (startIdx + i) % 7)
}

// ---------------------------------------------------------------------------
// Styled select wrapper (appearance-none + custom chevron)
// ---------------------------------------------------------------------------

function Select({ className, children, ...props }: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className="relative inline-flex">
      <select
        {...props}
        className={cn(
          "appearance-none rounded-md border bg-transparent py-2.5 pl-2 pr-7 text-sm sm:py-1.5 sm:text-xs",
          className,
        )}
      >
        {children}
      </select>
      <svg
        className="pointer-events-none absolute right-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground"
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="m6 9 6 6 6-6" />
      </svg>
    </div>
  )
}

// ---------------------------------------------------------------------------
// NumericInput — allows empty while editing, validates on blur
// ---------------------------------------------------------------------------

interface NumericInputProps {
  value: number | undefined
  onChange: (value: number) => void
  min?: number
  max?: number
  step?: number
  placeholder?: string
  className?: string
}

function NumericInput({ value, onChange, min = 0, max, step, placeholder, className }: NumericInputProps) {
  const [localValue, setLocalValue] = useState(value?.toString() ?? "")

  useEffect(() => {
    setLocalValue(value?.toString() ?? "")
  }, [value])

  return (
    <input
      type="text"
      inputMode="decimal"
      step={step}
      value={localValue}
      placeholder={placeholder}
      onChange={e => {
        const raw = e.target.value
        // Allow empty, digits, decimals, and minus
        if (raw === "" || raw === "-" || /^-?\d*\.?\d*$/.test(raw)) {
          setLocalValue(raw)
          const num = Number(raw)
          if (raw !== "" && raw !== "-" && !isNaN(num)) {
            onChange(num)
          }
        }
      }}
      onBlur={() => {
        const num = Number(localValue)
        if (localValue === "" || isNaN(num)) {
          // Reset to min on empty/invalid
          const resetVal = min ?? 0
          setLocalValue(resetVal.toString())
          onChange(resetVal)
        } else {
          let clamped = num
          if (min !== undefined) clamped = Math.max(min, clamped)
          if (max !== undefined) clamped = Math.min(max, clamped)
          setLocalValue(clamped.toString())
          if (clamped !== num) onChange(clamped)
        }
      }}
      className={cn("rounded-md border px-2 py-2.5 text-center text-sm sm:py-1 sm:text-xs", className)}
    />
  )
}

// ---------------------------------------------------------------------------
// TimeInput — time field with custom dropdown picker
// ---------------------------------------------------------------------------

interface TimeInputProps {
  value: string // "HH:MM" or ""
  onChange: (value: string) => void
}

function TimeInput({ value, onChange }: TimeInputProps) {
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const hourListRef = useRef<HTMLDivElement>(null)
  const minListRef = useRef<HTMLDivElement>(null)

  const [h, m] = (value || "09:00").split(":").map(Number) as [number, number]

  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close on outside click
  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [open])

  // Scroll to selected values when opening, then smooth-scroll dropdown into view
  useEffect(() => {
    if (!open) return
    requestAnimationFrame(() => {
      const scrollToIndex = (list: HTMLDivElement | null, index: number) => {
        if (!list || !list.children[0]) return
        const itemH = (list.children[0] as HTMLElement).offsetHeight
        list.scrollTop = index * itemH - list.clientHeight / 2 + itemH / 2
      }
      scrollToIndex(hourListRef.current, h)
      scrollToIndex(minListRef.current, m)
      // Smooth-scroll parent to reveal dropdown
      dropdownRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
    })
  }, [open, h, m])

  const selectHour = (newH: number) => {
    onChange(`${String(newH).padStart(2, "0")}:${String(m).padStart(2, "0")}`)
  }
  const selectMin = (newM: number) => {
    onChange(`${String(h).padStart(2, "0")}:${String(newM).padStart(2, "0")}`)
  }

  return (
    <div ref={containerRef} className="relative inline-block">
      {/* Display field */}
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="inline-flex items-center gap-1.5 rounded-md border bg-transparent px-2 py-2 text-sm font-medium tabular-nums sm:py-1 sm:text-xs"
      >
        {String(h).padStart(2, "0")}:{String(m).padStart(2, "0")}
        <svg className="size-3.5 text-muted-foreground" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="10" />
          <polyline points="12 6 12 12 16 14" />
        </svg>
      </button>

      {/* Dropdown — opens to the right, vertically centered */}
      {open && (
        <div ref={dropdownRef} className="absolute left-full top-1/2 z-50 ml-1 flex -translate-y-1/2 overflow-hidden rounded-lg border bg-popover shadow-lg">
          {/* Hours */}
          <div ref={hourListRef} className="flex h-48 w-12 flex-col overflow-y-auto scrollbar-none">
            {Array.from({ length: 24 }, (_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => selectHour(i)}
                className={cn(
                  "shrink-0 px-2 py-1.5 text-center text-sm tabular-nums transition-colors",
                  i === h
                    ? "bg-primary font-semibold text-primary-foreground"
                    : "hover:bg-muted",
                )}
              >
                {String(i).padStart(2, "0")}
              </button>
            ))}
          </div>
          {/* Minutes */}
          <div ref={minListRef} className="flex h-48 w-12 flex-col overflow-y-auto border-l scrollbar-none">
            {Array.from({ length: 60 }, (_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => selectMin(i)}
                className={cn(
                  "shrink-0 px-2 py-1.5 text-center text-sm tabular-nums transition-colors",
                  i === m
                    ? "bg-primary font-semibold text-primary-foreground"
                    : "hover:bg-muted",
                )}
              >
                {String(i).padStart(2, "0")}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Reminder offset helpers
// ---------------------------------------------------------------------------

/** Format an offset in ms to a human-readable label (e.g. "1 hour before") */
/** Format deadline offset DSL (e.g. "P1D" → "1 day after start") */
function formatDeadlineOffset(offset: string): string {
  const match = offset.match(/^P(\d+)D$/)
  if (!match) return offset
  const days = Number(match[1])
  if (days === 0) return "Same day"
  return `${days} day${days > 1 ? "s" : ""} after start`
}

const DEADLINE_PRESETS = [
  { label: "Same day", value: "P0D" },
  { label: "1 day after", value: "P1D" },
  { label: "2 days after", value: "P2D" },
  { label: "3 days after", value: "P3D" },
  { label: "5 days after", value: "P5D" },
  { label: "1 week after", value: "P7D" },
  { label: "2 weeks after", value: "P14D" },
] as const

// ---------------------------------------------------------------------------
// Tier type
// ---------------------------------------------------------------------------

type PickerTier = "quick" | "custom" | "advanced"

/** Determine the initial tier based on config complexity */
function inferTier(config?: Partial<RecurrenceConfig>): PickerTier {
  if (!config) return "quick"
  if (config.flexibleCount || config.progressiveField || config.targetContainerId ||
      config.pilePolicy === "ALLOW" || config.pilePolicy === "CAP" ||
      config.endType === "count" || config.endType === "date") return "advanced"
  if (config.interval && config.interval > 1) return "custom"
  if (config.weekdays && config.weekdays.length > 0) return "custom"
  if (config.monthDay || config.monthWeek != null) return "custom"
  if (config.mode === "AFTER_COMPLETION") return "custom"
  if (config.weekdayTimes) return "custom"
  return "quick"
}

// ---------------------------------------------------------------------------
// Summary helper — human-readable description of the config
// ---------------------------------------------------------------------------

function summarizeConfig(config: RecurrenceConfig): string {
  if (config.mode === "AFTER_COMPLETION") {
    return `${config.afterCompletionDays} ${config.afterCompletionDays === 1 ? "day" : "days"} after completion`
  }

  const freqLabelPlural = { daily: "days", weekly: "weeks", monthly: "months", yearly: "years" }[config.frequency]
  const intervalStr = config.interval > 1 ? `Every ${config.interval} ${freqLabelPlural}` : ""

  if (config.flexibleCount) {
    return `${config.flexibleCount}× per ${config.flexiblePeriod ?? "week"}`
  }

  if (config.frequency === "weekly" && config.weekdays.length > 0) {
    const days = config.weekdays.map(d => WEEKDAY_LABELS[d]!).join(", ")
    return config.interval > 1 ? `Every ${config.interval} weeks on ${days}` : `Every ${days}`
  }

  if (config.frequency === "monthly") {
    if (config.monthWeek != null && config.monthWeekday != null) {
      const weekLabel = config.monthWeek === -1 ? "last" :
        MONTH_WEEK_LABELS[MONTH_WEEK_VALUES.indexOf(config.monthWeek as typeof MONTH_WEEK_VALUES[number])]?.toLowerCase() ?? ""
      const dayLabel = WEEKDAY_LABELS[config.monthWeekday]
      return config.interval > 1 ? `Every ${config.interval} months on the ${weekLabel} ${dayLabel}` : `Monthly on the ${weekLabel} ${dayLabel}`
    }
    if (config.monthDay) {
      return config.interval > 1 ? `Every ${config.interval} months on day ${config.monthDay}` : `Monthly on day ${config.monthDay}`
    }
  }

  if (config.interval > 1) return intervalStr

  return { daily: "Every day", weekly: "Every week", monthly: "Every month", yearly: "Every year" }[config.frequency]
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const RecurrencePicker = memo(function RecurrencePicker({
  templateId,
  templateParentPath,
  initialConfig,
  taskStartAt,
  onSave,
  onCancel: _onCancel,
  onPause,
  isPaused,
  onDelete,
}: RecurrencePickerProps) {
  const { t } = useTranslation()
  const { value: weekStartSetting } = useSetting("#week-start", "-1")
  const orderedWeekdays = useMemo(() => getOrderedWeekdays(weekStartSetting), [weekStartSetting])
  const [config, setConfig] = useState<RecurrenceConfig>({ ...DEFAULT_CONFIG, ...initialConfig })
  const [tier, setTier] = useState<PickerTier>(inferTier(initialConfig))
  const [showPerDayTimes, setShowPerDayTimes] = useState(!!initialConfig?.weekdayTimes)
  const [showTemplates, setShowTemplates] = useState(false)
  const [nlFocused, setNlFocused] = useState(false)
  const [reminderPickerOpen, setReminderPickerOpen] = useState(false)
  const [deadlinePickerOpen, setDeadlinePickerOpen] = useState(false)
  const advancedRef = useRef<HTMLDivElement>(null)
  const customRef = useRef<HTMLDivElement>(null)

  // AI preview state
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)
  const [previewTaskId, setPreviewTaskId] = useState<string | null>(null)
  const [previewOpen, setPreviewOpen] = useState(false)

  // Fetch template task for AI context (title + note)
  const [templateTask] = useLiveQueryImmediate(
    () => templateId ? db.totodo.get(templateId) : undefined,
    [templateId],
    undefined,
  )

  const updateConfig = useCallback((partial: Partial<RecurrenceConfig>) => {
    setConfig(prev => ({ ...prev, ...partial }))
  }, [])

  // AI preview handler
  const handlePreview = useCallback(async () => {
    if (!config.aiPrompt || previewLoading) return
    setPreviewLoading(true)
    setPreviewError(null)
    try {
      // Gather previous instance titles if avoidRepeat is enabled
      let previousTitles: string[] | undefined
      if (config.aiAvoidRepeat !== false && templateId) {
        const instances = await db.totodo
          .where("recurringTemplateId")
          .equals(templateId)
          .filter((i) => !!i.title && i.isRecurrenceInstance === 1)
          .toArray()
        previousTitles = instances.map((i) => i.title!.slice(0, 100)).slice(-20)
      }

      const res = await apiRequest<{ ok: boolean; result: AiPreviewResult }>({
        path: "/ai/preview-enrichment",
        method: "POST",
        timeout: 60_000,
        body: {
          aiPrompt: config.aiPrompt,
          templateTitle: templateTask?.title,
          templateNote: templateTask?.note,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          previousTitles,
          creativity: config.aiCreativity,
          detailLevel: config.aiDetailLevel,
        },
      })
      if (res.data && "result" in res.data) {
        // Create temp task BEFORE opening modal — instant render on open
        const effectiveParentPath = templateParentPath || templateTask?.parentPath
        const taskId = await createAiPreviewTask(res.data.result, templateTask?.title ?? "", effectiveParentPath)
        setPreviewTaskId(taskId)
        setPreviewOpen(true)
      } else {
        // Quota reached / no AI on the plan: say which instead of "try again"
        setPreviewError(aiRefusalMessage(res.status, res.data, t) ?? t("recurrence.aiPreviewError", "Preview failed — try again"))
      }
    } catch {
      setPreviewError(t("recurrence.aiPreviewError", "Preview failed — try again"))
    } finally {
      setPreviewLoading(false)
    }
  }, [config.aiPrompt, previewLoading, templateTask?.title, templateTask?.note, templateParentPath, templateTask?.parentPath, t])

  // Handle reminder offset selection from RecurringReminderPicker
  const handleReminderOffsetSelect = useCallback((offset: ReminderOffset) => {
    setConfig(prev => ({
      ...prev,
      reminderOffsets: [...prev.reminderOffsets, offset],
    }))
  }, [])

  const removeReminder = useCallback((index: number) => {
    setConfig(prev => ({
      ...prev,
      reminderOffsets: prev.reminderOffsets.filter((_, i) => i !== index),
    }))
  }, [])

  const setFrequency = useCallback((freq: RecurrenceFrequency) => {
    setConfig(prev => ({
      ...prev,
      frequency: freq,
      weekdays: freq === "weekly" ? prev.weekdays : [],
      monthDay: undefined,
      monthWeek: undefined,
      monthWeekday: undefined,
    }))
  }, [])

  const handleApplyTemplate = useCallback((template: RecurrenceTemplate) => {
    setConfig(prev => ({
      ...prev,
      ...template.config,
      ...(template.progressive ? {
        progressiveField: template.progressive.field,
        progressiveStart: template.progressive.start,
        progressiveIncrement: template.progressive.increment,
        progressiveUnit: template.progressive.unit,
        progressivePer: template.progressive.per,
        progressiveMax: template.progressive.max,
      } : {}),
    }))
    setShowTemplates(false)
    // Jump to appropriate tier
    if (template.progressive || template.config.flexibleCount) setTier("advanced")
    else if (template.config.weekdays?.length || (template.config.interval && template.config.interval > 1)) setTier("custom")
  }, [])

  const [nlText, setNlText] = useState("")
  const nlPreview = nlText.length >= 3 ? parseRecurrenceNL(nlText) : null
  const [containerPickerOpen, setContainerPickerOpen] = useState(false)

  const handleNLInput = useCallback((text: string) => {
    const parsed = parseRecurrenceNL(text)
    if (!parsed) return
    setConfig(prev => ({
      ...prev,
      mode: parsed.mode === "AFTER_COMPLETION" ? "AFTER_COMPLETION" : "FIXED",
      frequency: parsed.frequency,
      interval: parsed.interval,
      ...(parsed.weekdays ? { weekdays: parsed.weekdays } : {}),
      ...(parsed.flexibleCount ? { flexibleCount: parsed.flexibleCount, flexiblePeriod: parsed.flexiblePeriod } : {}),
      ...(parsed.afterCompletionDays ? { afterCompletionDays: parsed.afterCompletionDays } : {}),
    }))
    setNlText("")
    // Auto-advance to custom if the parse produced something complex
    if (parsed.weekdays?.length || parsed.interval > 1 || parsed.mode === "AFTER_COMPLETION") {
      setTier("custom")
    }
  }, [])

  const [targetContainerTitle] = useLiveQueryImmediate(
    async () => {
      if (!config.targetContainerId) return undefined
      const c = await db.totodo.get(config.targetContainerId)
      return c?.title
    },
    [config.targetContainerId],
    undefined,
  )

  const summary = useMemo(() => summarizeConfig(config), [config])

  // Time label for the recurring task (used in reminder offset picker)
  // Round to nearest 5 min — startAt often stores "current time" (e.g. 12:59)
  // which makes offsets awkward. Rounding to 13:00 gives cleaner reminders.
  const taskTimeLabel = useMemo(() => {
    if (!taskStartAt) return undefined
    const d = dayjs(taskStartAt)
    const roundedMin = Math.round(d.minute() / 5) * 5
    return d.minute(roundedMin).second(0).format("HH:mm")
  }, [taskStartAt])

  const handleSave = useCallback(() => {
    onSave(config)
  }, [config, onSave])

  // Quick-tap handler for Tier 1 presets
  const handleQuickPreset = useCallback((freq: RecurrenceFrequency) => {
    setConfig(prev => ({
      ...prev,
      mode: "FIXED",
      frequency: freq,
      interval: 1,
      weekdays: [],
      monthDay: freq === "monthly" ? new Date().getDate() : undefined,
      monthWeek: undefined,
      monthWeekday: undefined,
    }))
  }, [])

  const handleWeekdaysPreset = useCallback(() => {
    setConfig(prev => ({
      ...prev,
      mode: "FIXED",
      frequency: "weekly",
      interval: 1,
      weekdays: [0, 1, 2, 3, 4],
    }))
  }, [])

  // Auto-scroll expanded sections into view
  useEffect(() => {
    if (tier === "advanced") {
      requestAnimationFrame(() => {
        advancedRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })
      })
    }
  }, [tier])

  useEffect(() => {
    if (tier === "custom") {
      requestAnimationFrame(() => {
        customRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" })
      })
    }
  }, [tier])

  // Monthly mode toggle: "day" or "weekday"
  const monthlyMode = config.monthWeek != null ? "weekday" : "day"

  return (
    <div className="flex flex-col gap-4">
      {/* ════════════════════════════════════════════════════════════════════
       *  Header — always visible
       * ════════════════════════════════════════════════════════════════════ */}
      <div className="flex items-center justify-center pb-2 pt-0">
        <h3 className="text-xl font-bold">{summary}</h3>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
       *  Quick presets — always visible
       * ════════════════════════════════════════════════════════════════════ */}
      <div className="space-y-2">
        <div className="flex items-center gap-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          <RepeatIcon className="size-3.5" /> {t("recurrence.frequency", "Frequency")}
        </div>
      {/* One-tap preset pills — hidden when custom tier is active */}
      {tier === "quick" && (
      <div className="grid grid-cols-2 gap-2">
        {([
          { freq: "daily" as const, label: t("recurrence.freq_daily", "Daily") },
          { freq: "weekly" as const, label: t("recurrence.freq_weekly", "Weekly") },
          { freq: "monthly" as const, label: t("recurrence.freq_monthly", "Monthly") },
          { freq: "yearly" as const, label: t("recurrence.freq_yearly", "Yearly") },
        ]).map(({ freq, label }) => (
          <button
            key={freq}
            onClick={() => handleQuickPreset(freq)}
            className={cn(
              "rounded-2xl px-3 py-2.5 text-[13px] font-medium transition-all",
              config.mode === "FIXED" && config.frequency === freq && config.interval === 1 && config.weekdays.length === 0
                ? "bg-primary/10 text-primary"
                : "bg-muted text-foreground hover:bg-muted/50",
            )}
          >
            {label}
          </button>
        ))}
      </div>
      )}

      {/* Extra quick presets + Custom toggle */}
      <div className="flex gap-2">
        {tier === "quick" && (
          <button
            onClick={handleWeekdaysPreset}
            className={cn(
              "flex-1 rounded-2xl px-3 py-2.5 text-[13px] font-medium transition-all",
              config.mode === "FIXED" && config.frequency === "weekly" && config.weekdays.length === 5 &&
                config.weekdays.every((d, i) => d === i)
                ? "bg-primary/10 text-primary"
                : "bg-muted text-muted-foreground hover:bg-muted/50",
            )}
          >
            {t("recurrence.weekdays", "Every weekday")}
          </button>
        )}
        <button
          onClick={() => setTier(tier === "quick" ? "custom" : "quick")}
          className={cn(
            "flex-1 rounded-2xl px-3 py-2.5 text-[13px] font-medium transition-all",
            (tier === "custom" || tier === "advanced")
              ? "bg-primary/10 text-primary"
              : "bg-muted text-muted-foreground hover:bg-muted/50",
          )}
        >
          {t("recurrence.custom", "Custom…")}
        </button>
      </div>

      {/* NL input — compact hint that expands on focus */}
      <div className="relative">
        {nlFocused ? (
          <input
            autoFocus
            type="text"
            value={nlText}
            onChange={(e) => setNlText(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && nlPreview) handleNLInput(nlText) }}
            onBlur={() => { if (!nlText) setNlFocused(false) }}
            placeholder={t("recurrence.nlPlaceholder", "e.g. \"every mon, wed, fri at 9\"")}
            className="w-full rounded-full bg-muted px-4 py-2.5 text-[13px] placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/20"
          />
        ) : (
          <button
            onClick={() => setNlFocused(true)}
            className="w-full rounded-full bg-muted/40 px-4 py-2.5 text-center text-[13px] text-muted-foreground/70 hover:bg-muted/60"
          >
            {t("recurrence.nlHint", "Or type: \"every monday at 9\"")}
          </button>
        )}
        {nlFocused && nlPreview && (
          <button
            onClick={() => handleNLInput(nlText)}
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded bg-primary px-2 py-0.5 text-[10px] font-medium text-primary-foreground"
          >
            {t("common.apply", "Apply")}
          </button>
        )}
        {nlFocused && nlText.length >= 3 && !nlPreview && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-muted-foreground">
            {t("recurrence.nlNoMatch", "couldn't parse")}
          </span>
        )}
      </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════════
       *  Custom schedule
       * ════════════════════════════════════════════════════════════════════ */}
      {(tier === "custom" || tier === "advanced") && (
        <div ref={customRef} className="flex flex-col gap-3">
          {/* Frequency segmented control */}
          <div className="grid grid-cols-4 gap-1 rounded-lg bg-muted p-1">
            {(["daily", "weekly", "monthly", "yearly"] as const).map(freq => (
              <button
                key={freq}
                onClick={() => setFrequency(freq)}
                className={cn(
                  "rounded-md px-2 py-1.5 text-xs font-medium transition-all",
                  config.frequency === freq
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t(`recurrence.freq_${freq}`, freq.charAt(0).toUpperCase() + freq.slice(1))}
              </button>
            ))}
          </div>

          {/* Interval */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">{t("recurrence.every", "Every")}</span>
            <NumericInput
              value={config.interval}
              onChange={v => updateConfig({ interval: v })}
              min={1}
              max={99}
              className="w-16 sm:w-14"
            />
            <span className="text-xs text-muted-foreground">
              {config.interval === 1
                ? ({ daily: "day", weekly: "week", monthly: "month", yearly: "year" }[config.frequency])
                : ({ daily: "days", weekly: "weeks", monthly: "months", yearly: "years" }[config.frequency])}
            </span>
          </div>

          {/* Weekday picker — weekly only */}
          {config.frequency === "weekly" && (
            <div className="flex flex-col gap-2">
              <div className="flex justify-between">
                {orderedWeekdays.map((weekdayIdx) => (
                  <button
                    key={weekdayIdx}
                    onClick={() => {
                      const weekdays = config.weekdays.includes(weekdayIdx)
                        ? config.weekdays.filter(d => d !== weekdayIdx)
                        : [...config.weekdays, weekdayIdx].sort()
                      updateConfig({ weekdays })
                    }}
                    className={cn(
                      "flex size-11 items-center justify-center rounded-full text-sm font-medium transition-colors sm:size-10 sm:text-xs",
                      config.weekdays.includes(weekdayIdx)
                        ? "bg-primary text-primary-foreground"
                        : "bg-muted text-muted-foreground hover:bg-muted/50",
                    )}
                  >
                    {WEEKDAY_LABELS[weekdayIdx]!.slice(0, 2)}
                  </button>
                ))}
              </div>

              {/* Per-day time override (collapsible) */}
              {config.weekdays.length > 1 && (
                <div>
                  <button
                    onClick={() => setShowPerDayTimes(!showPerDayTimes)}
                    className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
                  >
                    <ChevronLeftIcon className={cn("size-3 transition-transform", showPerDayTimes && "-rotate-90")} />
                    {t("recurrence.perDayTimes", "Set different times")}
                  </button>
                  {showPerDayTimes && (
                    <div className="mt-2 flex flex-col gap-1.5">
                      {config.weekdays.map(day => (
                        <div key={day} className="flex items-center gap-2">
                          <span className="w-8 text-[11px] font-medium">{WEEKDAY_LABELS[day]}</span>
                          <TimeInput
                            value={config.weekdayTimes?.[day] ?? ""}
                            onChange={v => {
                              const times = { ...(config.weekdayTimes ?? {}) }
                              if (v) {
                                times[day] = v
                              } else {
                                delete times[day]
                              }
                              updateConfig({ weekdayTimes: Object.keys(times).length > 0 ? times : undefined })
                            }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Monthly options — day of month OR nth weekday */}
          {config.frequency === "monthly" && (
            <div className="flex flex-col gap-3">
              <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
                <button
                  onClick={() => updateConfig({ monthWeek: undefined, monthWeekday: undefined, monthDay: config.monthDay ?? new Date().getDate() })}
                  className={cn(
                    "rounded-md px-2 py-1.5 text-xs font-medium transition-all",
                    monthlyMode === "day" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground",
                  )}
                >
                  {t("recurrence.onDay", "On day")}
                </button>
                <button
                  onClick={() => updateConfig({ monthDay: undefined, monthWeek: config.monthWeek ?? 1, monthWeekday: config.monthWeekday ?? 0 })}
                  className={cn(
                    "rounded-md px-2 py-1.5 text-xs font-medium transition-all",
                    monthlyMode === "weekday" ? "bg-background text-foreground shadow-sm" : "text-muted-foreground",
                  )}
                >
                  {t("recurrence.onThe", "On the")}
                </button>
              </div>

              {monthlyMode === "day" ? (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{t("recurrence.dayOfMonth", "Day")}</span>
                  <NumericInput
                    value={config.monthDay ?? new Date().getDate()}
                    onChange={v => updateConfig({ monthDay: v })}
                    min={1}
                    max={31}
                    className="w-16 sm:w-14"
                  />
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Select
                    value={config.monthWeek ?? 1}
                    onChange={e => updateConfig({ monthWeek: Number(e.target.value) })}
                  >
                    {MONTH_WEEK_VALUES.map((val, i) => (
                      <option key={val} value={val}>{MONTH_WEEK_LABELS[i]}</option>
                    ))}
                  </Select>
                  <Select
                    value={config.monthWeekday ?? 0}
                    onChange={e => updateConfig({ monthWeekday: Number(e.target.value) })}
                  >
                    {WEEKDAY_LABELS.map((label, i) => (
                      <option key={i} value={i}>{label}</option>
                    ))}
                  </Select>
                </div>
              )}
            </div>
          )}

          {/* After completion — segmented control */}
          <div className="flex flex-col gap-1.5">
            <span className="text-[11px] font-medium text-muted-foreground/70">{t("recurrence.repeatType", "Repeat type")}</span>
            <div className="grid grid-cols-2 gap-1 rounded-lg bg-muted p-1">
            <button
              type="button"
              onClick={() => updateConfig({ mode: "FIXED" })}
              className={cn(
                "rounded-md px-2 py-2 text-xs font-medium transition-all",
                config.mode === "FIXED"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t("recurrence.fixed", "Fixed schedule")}
            </button>
            <button
              type="button"
              onClick={() => updateConfig({ mode: "AFTER_COMPLETION" })}
              className={cn(
                "rounded-md px-2 py-2 text-xs font-medium transition-all",
                config.mode === "AFTER_COMPLETION"
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {t("recurrence.afterCompletion", "After completion")}
            </button>
          </div>
          </div>

          {config.mode === "AFTER_COMPLETION" && (
            <div className="flex items-center gap-2 pl-2">
              <NumericInput
                value={config.afterCompletionDays}
                onChange={v => updateConfig({ afterCompletionDays: v })}
                min={1}
                max={365}
                className="w-16 sm:w-14"
              />
              <span className="text-xs text-muted-foreground">
                {t("recurrence.daysAfter", "days after completion")}
              </span>
            </div>
          )}

          {/* End condition — segmented control */}
          <div className="flex flex-col gap-2">
            <span className="text-[11px] font-medium text-muted-foreground/70">{t("recurrence.ends", "Ends")}</span>
            <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted p-1">
              <button
                type="button"
                onClick={() => updateConfig({ endType: "never" })}
                className={cn(
                  "rounded-md px-2 py-2 text-xs font-medium transition-all",
                  config.endType === "never"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t("recurrence.endNever", "Never")}
              </button>
              <button
                type="button"
                onClick={() => updateConfig({ endType: "count" })}
                className={cn(
                  "rounded-md px-2 py-2 text-xs font-medium transition-all",
                  config.endType === "count"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t("recurrence.endCount", "Count")}
              </button>
              <button
                type="button"
                onClick={() => updateConfig({ endType: "date" })}
                className={cn(
                  "rounded-md px-2 py-2 text-xs font-medium transition-all",
                  config.endType === "date"
                    ? "bg-background text-foreground shadow-sm"
                    : "text-muted-foreground hover:text-foreground",
                )}
              >
                {t("recurrence.endDate", "Date")}
              </button>
            </div>
            {config.endType === "count" && (
              <div className="flex items-center gap-2 pl-2">
                <span className="text-xs text-muted-foreground">{t("recurrence.stopAfter", "Stop after")}</span>
                <NumericInput
                  value={config.endCount ?? 10}
                  onChange={v => updateConfig({ endCount: v })}
                  min={1}
                  className="w-16 sm:w-14"
                />
                <span className="text-xs text-muted-foreground">{t("recurrence.times", "times")}</span>
              </div>
            )}
            {config.endType === "date" && (
              <InfiniteCalendar
                selectedDate={config.endDate ? dayjs(Number(config.endDate)) : undefined}
                onSelect={(date) => updateConfig({ endDate: String(date.valueOf()) })}
                minDate={dayjs().add(1, "day")}
                height={180}
              />
            )}
          </div>

          {/* ── Advanced options — fold-out inside Custom ──────────────── */}
          <button
            type="button"
            onClick={() => setTier(tier === "advanced" ? "custom" : "advanced")}
            className="flex items-center gap-1.5 px-0.5 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70 transition-colors hover:text-muted-foreground"
          >
            <LightningIcon className="size-3.5" />
            {t("recurrence.advancedOptions", "Advanced")}
            <ChevronLeftIcon className={cn("size-3 transition-transform", tier === "advanced" && "-rotate-90")} />
          </button>

          {tier === "advanced" && (
            <div ref={advancedRef} className="flex flex-col gap-4">
          {/* Templates */}
          <div>
            <button
              type="button"
              onClick={() => setShowTemplates(!showTemplates)}
              className="flex items-center gap-1.5 px-0.5 pt-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70 transition-colors hover:text-muted-foreground"
            >
              <GridIcon className="size-3.5" />
              {t("recurrence.templates", "Browse templates")}
              <ChevronLeftIcon className={cn("size-3 transition-transform", showTemplates && "-rotate-90")} />
            </button>
            {showTemplates && (
              <div className="mt-3 flex flex-col gap-3">
                {TEMPLATE_CATEGORIES.map(cat => {
                  const templates = RECURRENCE_TEMPLATES.filter(tmpl => tmpl.category === cat.id)
                  if (templates.length === 0) return null
                  return (
                    <div key={cat.id}>
                      <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
                        {cat.label}
                      </p>
                      <div className="flex flex-wrap gap-1.5">
                        {templates.map(tmpl => (
                          <button
                            key={tmpl.id}
                            type="button"
                            onClick={() => handleApplyTemplate(tmpl)}
                            className="rounded-full bg-muted px-3 py-2 text-[13px] font-medium transition-colors hover:bg-muted/50"
                            title={tmpl.description}
                          >
                            <span className="mr-1">{tmpl.icon}</span>
                            {tmpl.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          {/* Pile-up policy — segmented control */}
          <fieldset className="flex flex-col gap-3">
            <legend className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {t("recurrence.limits", "Limits")}
            </legend>
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-3 gap-1 rounded-lg bg-muted p-1">
                <button
                  type="button"
                  onClick={() => updateConfig({ pilePolicy: "NO_PILE" })}
                  className={cn(
                    "rounded-md px-2 py-2 text-xs font-medium transition-all",
                    config.pilePolicy === "NO_PILE"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t("recurrence.pileNoPile", "Max 1")}
                </button>
                <button
                  type="button"
                  onClick={() => updateConfig({ pilePolicy: "ALLOW" })}
                  className={cn(
                    "rounded-md px-2 py-2 text-xs font-medium transition-all",
                    config.pilePolicy === "ALLOW"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t("recurrence.pileAllow", "Allow all")}
                </button>
                <button
                  type="button"
                  onClick={() => updateConfig({ pilePolicy: "CAP" })}
                  className={cn(
                    "rounded-md px-2 py-2 text-xs font-medium transition-all",
                    config.pilePolicy === "CAP"
                      ? "bg-background text-foreground shadow-sm"
                      : "text-muted-foreground hover:text-foreground",
                  )}
                >
                  {t("recurrence.pileCap", "Cap")}
                </button>
              </div>
              {config.pilePolicy === "CAP" && (
                <div className="flex items-center gap-2 pl-2">
                  <span className="text-xs text-muted-foreground">{t("recurrence.maxOpen", "Max open")}</span>
                  <NumericInput
                    value={config.pileCap}
                    onChange={v => updateConfig({ pileCap: v })}
                    min={2}
                    max={99}
                    className="w-16 sm:w-14"
                  />
                </div>
              )}
            </div>
          </fieldset>

          {/* Target container */}
          <fieldset className="flex flex-col gap-3">
            <legend className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
              {t("recurrence.targetContainer", "Place instances in")}
            </legend>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setContainerPickerOpen(true)}
                className={cn(
                  "flex-1 rounded-lg px-3 py-2 text-left text-[13px] transition-colors hover:bg-muted/50",
                  config.targetContainerId ? "bg-primary/10 text-foreground" : "bg-muted text-muted-foreground",
                )}
              >
                {config.targetContainerId
                  ? targetContainerTitle ?? "…"
                  : t("recurrence.sameAsTemplate", "Same as template (default)")}
              </button>
              {config.targetContainerId && (
                <button
                  type="button"
                  onClick={() => updateConfig({ targetContainerId: undefined })}
                  className="flex size-6 items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
                >
                  <CrossIcon className="size-3.5" />
                </button>
              )}
            </div>
          </fieldset>

          {/* Flexible goal */}
          <div className="flex flex-col gap-2 rounded-2xl bg-muted/30 p-3">
            <label className="flex items-center justify-between">
              <span className="text-xs font-medium">{t("recurrence.flexibleGoal", "Flexible goal")}</span>
              <Switch
                checked={!!config.flexibleCount}
                onCheckedChange={(checked) => {
                  if (checked) {
                    updateConfig({ flexibleCount: 3, flexiblePeriod: "week", weekdays: [] })
                  } else {
                    updateConfig({ flexibleCount: undefined, flexiblePeriod: undefined, flexibleResetDay: undefined })
                  }
                }}
              />
            </label>
            {config.flexibleCount && (
              <div className="flex items-center gap-2">
                <NumericInput
                  value={config.flexibleCount}
                  onChange={v => updateConfig({ flexibleCount: v })}
                  min={1}
                  max={14}
                  className="w-14 sm:w-12"
                />
                <span className="text-xs text-muted-foreground">
                  {t("recurrence.timesPer", "times per")}
                </span>
                <Select
                  value={config.flexiblePeriod ?? "week"}
                  onChange={e => updateConfig({ flexiblePeriod: e.target.value as "week" | "month" })}
                >
                  <option value="week">{t("recurrence.week", "week")}</option>
                  <option value="month">{t("recurrence.month", "month")}</option>
                </Select>
              </div>
            )}

            {/* Progressive overload */}
            <label className="flex items-center justify-between pt-1">
              <span className="text-xs font-medium">{t("recurrence.progressive", "Progressive overload")}</span>
              <Switch
                checked={!!config.progressiveField}
                onCheckedChange={(checked) => {
                  if (checked) {
                    updateConfig({ progressiveField: "distance", progressiveStart: 3, progressiveIncrement: 0.5, progressiveUnit: "km", progressivePer: "week" })
                  } else {
                    updateConfig({ progressiveField: undefined, progressiveStart: undefined, progressiveIncrement: undefined, progressiveUnit: undefined, progressiveMax: undefined, progressivePer: undefined })
                  }
                }}
              />
            </label>
            {config.progressiveField && (
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <Select
                    value={config.progressiveField}
                    onChange={e => updateConfig({ progressiveField: e.target.value })}
                  >
                    <option value="distance">{t("recurrence.prog.distance", "Distance")}</option>
                    <option value="duration">{t("recurrence.prog.duration", "Duration")}</option>
                    <option value="reps">{t("recurrence.prog.reps", "Reps")}</option>
                    <option value="weight">{t("recurrence.prog.weight", "Weight")}</option>
                  </Select>
                  <span className="text-xs text-muted-foreground">{t("recurrence.prog.startAt", "Start:")}</span>
                  <NumericInput
                    value={config.progressiveStart ?? 0}
                    onChange={v => updateConfig({ progressiveStart: v })}
                    min={0}
                    step={0.5}
                    className="w-16 sm:w-14"
                  />
                  <input
                    type="text"
                    value={config.progressiveUnit ?? "km"}
                    onChange={e => updateConfig({ progressiveUnit: e.target.value })}
                    className="w-14 rounded-md border px-2 py-2.5 text-center text-sm sm:w-12 sm:py-1 sm:text-xs"
                    placeholder="km"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">+</span>
                  <NumericInput
                    value={config.progressiveIncrement ?? 0}
                    onChange={v => updateConfig({ progressiveIncrement: v })}
                    min={0}
                    step={0.1}
                    className="w-16 sm:w-14"
                  />
                  <span className="text-xs text-muted-foreground">{t("recurrence.prog.every", "every")}</span>
                  <Select
                    value={config.progressivePer ?? "week"}
                    onChange={e => updateConfig({ progressivePer: e.target.value as "instance" | "week" | "month" })}
                  >
                    <option value="instance">{t("recurrence.prog.perInstance", "instance")}</option>
                    <option value="week">{t("recurrence.prog.perWeek", "week")}</option>
                    <option value="month">{t("recurrence.prog.perMonth", "month")}</option>
                  </Select>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">{t("recurrence.prog.max", "Max:")}</span>
                  <NumericInput
                    value={config.progressiveMax}
                    onChange={v => updateConfig({ progressiveMax: v })}
                    min={0}
                    step={0.5}
                    placeholder="∞"
                    className="w-16 sm:w-14"
                  />
                </div>
              </div>
            )}
          </div>
            </div>
          )}
        </div>
      )}

      <ContainerPicker
        open={containerPickerOpen}
        onOpenChange={setContainerPickerOpen}
        onSelect={(containerId) => {
          updateConfig({ targetContainerId: containerId })
          setContainerPickerOpen(false)
        }}
        currentContainerId={config.targetContainerId}
        showVirtualContainers={false}
      />

      {/* ── Reminder per occurrence ──────────────────────────────────── */}
      <div className="space-y-1">
        <div className="flex items-center gap-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          <BellIcon className="size-3.5" /> {t("recurrence.reminder", "Reminder")}
        </div>
        {config.reminderOffsets.map((r, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setReminderPickerOpen(true)}
            className="group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] transition-colors hover:bg-muted/50"
          >
            {r.kind === "alarm" ? (
              <AlarmIcon className="size-[22px] text-muted-foreground" />
            ) : (
              <BellIcon className="size-[22px] text-muted-foreground" />
            )}
            <span className="flex-1 text-left font-medium">
              {formatReminderOffsetWithTime(r.offsetMs, taskTimeLabel)}
            </span>
            <span
              onClick={(e) => {
                e.stopPropagation()
                removeReminder(i)
              }}
              className="flex size-6 items-center justify-center rounded-full opacity-0 transition-opacity hover:bg-muted group-hover:opacity-100"
            >
              <CrossIcon className="size-3.5 text-muted-foreground" />
            </span>
          </button>
        ))}
        <button
          type="button"
          onClick={() => setReminderPickerOpen(true)}
          className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] transition-colors hover:bg-muted/50"
        >
          <BellAddIcon className="size-[22px] text-muted-foreground" />
          <span className="flex-1 text-left font-medium text-muted-foreground">
            {t("recurrence.addReminder", "Add Reminder")}
          </span>
        </button>
      </div>

      {/* Reminder picker — recurring mode */}
      <RecurringReminderPicker
        taskTimeLabel={taskTimeLabel}
        open={reminderPickerOpen}
        onOpenChange={setReminderPickerOpen}
        onSelect={handleReminderOffsetSelect}
      />

      {/* ── Deadline per occurrence ──────────────────────────────────── */}
      <div className="space-y-1">
        <div className="flex items-center gap-1.5 px-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground/70">
          <FlagIcon className="size-3.5" /> {t("recurrence.deadline", "Deadline")}
        </div>
        {config.deadlineOffset != null ? (
          <button
            type="button"
            onClick={() => setDeadlinePickerOpen(true)}
            className="group flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] transition-colors hover:bg-muted/50"
          >
            <FlagIcon className="size-[22px] text-muted-foreground" />
            <span className="flex-1 text-left font-medium">
              {formatDeadlineOffset(config.deadlineOffset)}
            </span>
            <span
              onClick={(e) => {
                e.stopPropagation()
                updateConfig({ deadlineOffset: undefined })
              }}
              className="flex size-6 items-center justify-center rounded-full opacity-0 transition-opacity hover:bg-muted group-hover:opacity-100"
            >
              <CrossIcon className="size-3.5 text-muted-foreground" />
            </span>
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setDeadlinePickerOpen(true)}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] transition-colors hover:bg-muted/50"
          >
            <FlagAddIcon className="size-[22px] text-muted-foreground" />
            <span className="flex-1 text-left font-medium text-muted-foreground">
              {t("recurrence.addDeadline", "Add Deadline")}
            </span>
          </button>
        )}
      </div>

      {/* Deadline picker sheet */}
      <ResponsiveSheet
        open={deadlinePickerOpen}
        onOpenChange={setDeadlinePickerOpen}
        title={t("recurrence.deadline", "Deadline")}
      >
        <div className="flex flex-col gap-2 px-4 pb-6 pt-0">
          <h2 className="pb-3 pt-0 text-center text-xl font-bold">
            {t("recurrence.deadlineTitle", "Deadline offset")}
          </h2>
          <div className="grid grid-cols-2 gap-2">
            {DEADLINE_PRESETS.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => {
                  updateConfig({ deadlineOffset: p.value })
                  setDeadlinePickerOpen(false)
                }}
                className={cn(
                  "rounded-2xl px-3 py-2.5 text-[13px] font-medium transition-all active:scale-[0.97]",
                  config.deadlineOffset === p.value
                    ? "bg-primary/10 text-primary"
                    : "bg-muted text-foreground hover:bg-muted/80",
                )}
              >
                {p.label}
              </button>
            ))}
          </div>
          {config.deadlineOffset != null && (
            <button
              type="button"
              onClick={() => {
                updateConfig({ deadlineOffset: undefined })
                setDeadlinePickerOpen(false)
              }}
              className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-lg px-3 py-2.5 text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
            >
              <CrossIcon className="size-4" />
              {t("recurrence.removeDeadline", "Remove deadline")}
            </button>
          )}
        </div>
      </ResponsiveSheet>

      {/* Always in Focus toggle */}
      <div className="flex items-center justify-between rounded-2xl bg-muted/30 px-3 py-2.5">
        <span className="text-sm font-medium">
          {t("recurrence.alwaysInFocus", "Always in Focus")}
        </span>
        <Switch
          checked={config.autoFocus ?? false}
          onCheckedChange={(checked) => updateConfig({ autoFocus: checked })}
        />
      </div>

      {/* AI Enrichment toggle + prompt */}
      <div className="space-y-2 rounded-2xl bg-muted/30 px-3 py-2.5">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1.5 text-sm font-medium">
            <SparkleAiIcon className="size-4 text-foreground/60" />
            {t("recurrence.aiEnrich", "AI Enrich")}
          </span>
          <Switch
            checked={config.aiEnabled ?? false}
            onCheckedChange={(checked) => updateConfig({ aiEnabled: checked })}
          />
        </div>
        {config.aiEnabled && (
          <>
            <textarea
              autoFocus
              value={config.aiPrompt ?? ""}
              onChange={(e) => { updateConfig({ aiPrompt: e.target.value.slice(0, 500) }); setPreviewError(null) }}
              disabled={previewLoading}
              placeholder={t("recurrence.aiPromptPlaceholder", "Describe what AI should generate for each instance...")}
              rows={3}
              className={cn(
                "w-full resize-none rounded-xl border-none bg-muted px-3 py-2.5 text-[13px] text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-0 disabled:opacity-60",
                previewError && "ring-1 ring-destructive/60",
              )}
            />
            {/* Toolbar below textarea — ChatGPT style */}
            <div className="flex items-center justify-between -mt-0.5">
              <Popover>
                <PopoverTrigger asChild>
                  <button className="flex size-7 items-center justify-center rounded-lg text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground">
                    <SettingsIcon className="size-3.5" />
                  </button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-64 space-y-3 p-3">
                  {/* Creativity */}
                  <div className="space-y-1.5">
                    <span className="text-xs font-medium text-muted-foreground">{t("recurrence.aiCreativity", "Creativity")}</span>
                    <div className="flex gap-1">
                      {(["focused", "creative", "wild"] as const).map((level) => (
                        <button
                          key={level}
                          onClick={() => updateConfig({ aiCreativity: level })}
                          className={cn(
                            "flex-1 rounded-lg px-2 py-1.5 text-[11px] font-medium transition-colors",
                            (config.aiCreativity ?? "creative") === level
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground hover:bg-muted/80",
                          )}
                        >
                          {level === "focused" ? t("recurrence.aiFocused", "Focused") : level === "creative" ? t("recurrence.aiCreative", "Creative") : t("recurrence.aiWild", "Wild")}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Detail level */}
                  <div className="space-y-1.5">
                    <span className="text-xs font-medium text-muted-foreground">{t("recurrence.aiDetailLevel", "Detail level")}</span>
                    <div className="flex gap-1">
                      {(["short", "normal", "detailed"] as const).map((level) => (
                        <button
                          key={level}
                          onClick={() => updateConfig({ aiDetailLevel: level })}
                          className={cn(
                            "flex-1 rounded-lg px-2 py-1.5 text-[11px] font-medium transition-colors",
                            (config.aiDetailLevel ?? "normal") === level
                              ? "bg-primary text-primary-foreground"
                              : "bg-muted text-muted-foreground hover:bg-muted/80",
                          )}
                        >
                          {level === "short" ? t("recurrence.aiShort", "Short") : level === "normal" ? t("recurrence.aiNormal", "Normal") : t("recurrence.aiDetailed", "Detailed")}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Avoid repeats */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium text-muted-foreground">{t("recurrence.aiAvoidRepeat", "Avoid repeats")}</span>
                    <Switch
                      checked={config.aiAvoidRepeat !== false}
                      onCheckedChange={(checked) => updateConfig({ aiAvoidRepeat: checked })}
                    />
                  </div>
                </PopoverContent>
              </Popover>
              <button
                onClick={handlePreview}
                disabled={!config.aiPrompt?.trim() || previewLoading}
                className={cn(
                  "flex items-center gap-1 rounded-lg px-2.5 py-1 text-[11px] font-medium transition-all",
                  config.aiPrompt?.trim() && !previewLoading
                    ? "text-foreground/70 hover:text-foreground active:scale-95"
                    : "cursor-not-allowed text-muted-foreground/40",
                )}
              >
                {previewLoading ? (
                  <span className="size-3 animate-spin rounded-full border-[1.5px] border-current border-t-transparent" />
                ) : (
                  <PlayIcon className="size-3" />
                )}
                {t("recurrence.aiPreviewBtn", "Preview")}
              </button>
            </div>
            {previewError && (
              <p className="text-[11px] text-destructive/80 -mt-0.5">{previewError}</p>
            )}
          </>
        )}
      </div>

      {/* Save + Pause/Resume + Delete buttons */}
      <div className="flex items-center gap-2">
        {onPause && (
          <button
            onClick={onPause}
            title={isPaused ? t("recurrence.resume", "Resume") : t("recurrence.pause", "Pause")}
            className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-muted text-foreground/70 transition-all hover:bg-muted/80 active:scale-95"
          >
            {isPaused
              ? <PlayIcon className="size-6" />
              : <PauseIcon className="size-6" />
            }
          </button>
        )}
        <button
          onClick={handleSave}
          className="flex-1 rounded-2xl bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          {t("recurrence.saveRepeat", "Repeat {{summary}}", { summary })}
        </button>
        {onDelete && (
          <button
            onClick={onDelete}
            title={t("recurrence.remove", "Remove")}
            className="flex size-11 shrink-0 items-center justify-center rounded-2xl bg-muted text-foreground/70 transition-all hover:bg-destructive/10 hover:text-destructive active:scale-95"
          >
            <TrashIcon className="size-5" />
          </button>
        )}
      </div>

      {/* AI Preview Modal */}
      <AiPreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        taskId={previewTaskId}
      />

      {/* Safe area spacer for Capacitor */}
      <div className="safe-area-bottom" />
    </div>
  )
})

// ---------------------------------------------------------------------------
// Utility: Convert RecurrenceConfig → CreateRecurrenceOpts
// ---------------------------------------------------------------------------

const RRULE_FREQ_MAP: Record<RecurrenceFrequency, number> = {
  daily: RRule.DAILY,
  weekly: RRule.WEEKLY,
  monthly: RRule.MONTHLY,
  yearly: RRule.YEARLY,
}

const RRULE_WEEKDAY = [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR, RRule.SA, RRule.SU]

/**
 * Build an RRULE string from RecurrenceConfig.
 */
export function buildRruleFromConfig(config: RecurrenceConfig, dtstart: Date): string {
  const options: Partial<ConstructorParameters<typeof RRule>[0]> = {
    freq: RRULE_FREQ_MAP[config.frequency],
    interval: config.interval,
    dtstart,
  }

  if (config.frequency === "weekly" && config.weekdays.length > 0) {
    options.byweekday = config.weekdays.map(d => RRULE_WEEKDAY[d]!)
  }

  if (config.frequency === "monthly") {
    if (config.monthDay) {
      options.bymonthday = [config.monthDay]
    } else if (config.monthWeek != null && config.monthWeekday != null) {
      options.byweekday = [RRULE_WEEKDAY[config.monthWeekday]!.nth(config.monthWeek)]
    }
  }

  if (config.endType === "count" && config.endCount) {
    options.count = config.endCount
  }
  if (config.endType === "date" && config.endDate) {
    options.until = new Date(Number(config.endDate))
  }

  const rule = new RRule(options as ConstructorParameters<typeof RRule>[0])
  return rule.toString()
}

/**
 * Convert UI config to CreateRecurrenceOpts for the operations layer.
 */
export function configToRecurrenceOpts(
  templateId: string,
  config: RecurrenceConfig,
  timezone: string,
  startAtMs: number,
): CreateRecurrenceOpts {
  const dtstart = new Date(startAtMs)
  const rrule = config.mode === "FIXED" ? buildRruleFromConfig(config, dtstart) : undefined

  return {
    templateId,
    mode: config.mode,
    timezone,
    startAt: String(startAtMs),
    rrule,
    afterCompletionDays: config.mode === "AFTER_COMPLETION" ? config.afterCompletionDays : undefined,
    afterCompletionTime: config.afterCompletionTime,
    weekdayTimes: config.weekdayTimes ? JSON.stringify(config.weekdayTimes) : undefined,
    pilePolicy: config.pilePolicy,
    pileCap: config.pilePolicy === "CAP" ? config.pileCap : undefined,
    endDate: config.endType === "date" ? config.endDate : undefined,
    maxCount: config.endType === "count" ? config.endCount : undefined,
    deadlineOffset: config.deadlineOffset,
    showInTodayDaysBefore: config.showInTodayDaysBefore,
    reminderOffsetMs: config.reminderOffsets.length > 0 ? config.reminderOffsets[0]!.offsetMs : undefined,
    reminderKind: config.reminderOffsets.length > 0 ? config.reminderOffsets[0]!.kind : undefined,
    reminderOffsets: config.reminderOffsets.length > 0 ? config.reminderOffsets : undefined,
    targetContainerId: config.targetContainerId,
    flexibleCount: config.flexibleCount,
    flexiblePeriod: config.flexiblePeriod,
    flexibleResetDay: config.flexibleResetDay,
    progressiveField: config.progressiveField,
    progressiveStart: config.progressiveStart,
    progressiveIncrement: config.progressiveIncrement,
    progressiveUnit: config.progressiveUnit,
    progressiveMax: config.progressiveMax,
    progressivePer: config.progressivePer,
    autoFocus: config.autoFocus ? 1 : 0,
    aiEnabled: config.aiEnabled ? 1 : 0,
    aiPrompt: config.aiPrompt || undefined,
    aiCreativity: config.aiCreativity || undefined,
    aiAvoidRepeat: config.aiAvoidRepeat !== false ? 1 : 0,
    aiDetailLevel: config.aiDetailLevel || undefined,
  }
}
